import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { InvitacionDemo } from '../types/admin.types';

/** Hotfix 4-nov (decisión 2 del 3-nov): el plazo de la prueba, como lo acepta el servidor. */
const DIAS_POR_OMISION = 15;
const MINIMO_DIAS = 1;
const MAXIMO_DIAS = 365;
/** El largo de la columna `socio` en el servidor. */
const LARGO_DEL_SOCIO = 60;

/** `YYYY-MM-DD` en hora local, para el campo de fecha. */
function aFechaLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** La invitación vale todo el día elegido: vence al final de ese día, hora local. */
function alFinalDelDia(fecha: string): string {
  const [a, m, d] = fecha.split('-').map(Number);
  return new Date(a, m - 1, d, 23, 59, 59).toISOString();
}

/**
 * El servidor valida el código y el vencimiento con textos pensados para una persona
 * («La fecha de vencimiento tiene que ser futura.»), pero un 422 llega con el nombre
 * del campo delante y `mensajeDeError` lo descarta. Acá se rescata ese texto, sólo de
 * esos campos (los demás siguen con el mensaje genérico).
 */
const TEXTO_PROPIO_422 = /^(?:venceEn|codigo|diasDePrueba|socio): (.+)$/;
function mensajeDelServidor(e: unknown, porDefecto: string): string {
  const r = (e as { response?: { status?: number; data?: { error?: unknown } } })?.response;
  if (r?.status === 422 && typeof r.data?.error === 'string') {
    const primero = r.data.error.split(/, (?=\w+: )/)[0];
    const m = TEXTO_PROPIO_422.exec(primero);
    if (m) return m[1];
  }
  return mensajeDeError(e, porDefecto);
}

/**
 * Crear o editar una invitación de la demo. Con `invitacion` edita (sin el campo de
 * código: el enlace ya anda por ahí y no cambia); sin ella crea una nueva.
 */
export function InvitacionDemoDialog({
  abierto,
  invitacion,
  onCerrar,
  onGuardada,
}: {
  abierto: boolean;
  invitacion: InvitacionDemo | null;
  onCerrar: () => void;
  onGuardada: (guardada: InvitacionDemo) => void;
}) {
  const edita = invitacion !== null;
  const [nombre, setNombre] = useState('');
  const [modoCodigo, setModoCodigo] = useState<'generar' | 'elegir'>('generar');
  const [codigo, setCodigo] = useState('');
  const [tope, setTope] = useState('');
  const [vence, setVence] = useState('');
  const [dias, setDias] = useState(String(DIAS_POR_OMISION));
  const [socio, setSocio] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Cada vez que se abre arranca de cero (o con los datos de la invitación que se edita).
  useEffect(() => {
    if (!abierto) return;
    setNombre(invitacion?.nombre ?? '');
    setModoCodigo('generar');
    setCodigo('');
    setTope(invitacion?.topeDeAltas != null ? String(invitacion.topeDeAltas) : '');
    setVence(aFechaLocal(invitacion?.venceEn ?? null));
    setDias(String(invitacion?.diasDePrueba ?? DIAS_POR_OMISION));
    setSocio(invitacion?.socio ?? '');
    setError(null);
  }, [abierto, invitacion]);

  const guardar = async () => {
    if (guardando) return;
    if (!nombre.trim()) {
      setError('Pon un nombre para reconocerla.');
      return;
    }
    let topeDeAltas: number | null = null;
    if (tope.trim() !== '') {
      topeDeAltas = Number(tope);
      if (!Number.isInteger(topeDeAltas) || topeDeAltas < 1) {
        setError('El tope de altas tiene que ser un número entero de 1 en adelante.');
        return;
      }
    }
    const diasDePrueba = Number(dias);
    if (
      dias.trim() === '' ||
      !Number.isInteger(diasDePrueba) ||
      diasDePrueba < MINIMO_DIAS ||
      diasDePrueba > MAXIMO_DIAS
    ) {
      setError(`Los días de prueba van de ${MINIMO_DIAS} a ${MAXIMO_DIAS}, en números enteros.`);
      return;
    }
    // Vacío = sin socio (el pase muestra el texto genérico).
    const socioLimpio = socio.trim().replace(/\s+/g, ' ');
    const socioAEnviar = socioLimpio === '' ? null : socioLimpio;
    if (!edita && modoCodigo === 'elegir' && !codigo.trim()) {
      setError('Escribe el código que quieres usar, o elige que se genere solo.');
      return;
    }
    const venceEn = vence ? alFinalDelDia(vence) : null;
    // Al editar, el vencimiento sólo viaja si la persona lo cambió: una invitación vencida
    // tiene que poder renombrarse sin que el servidor rechace la fecha vieja.
    const cambioElVencimiento = !edita || vence !== aFechaLocal(invitacion.venceEn);
    // Al editar, el plazo y el socio sólo viajan si cambiaron (y sólo valen para las altas que vengan).
    const cambioElPlazo = !edita || diasDePrueba !== invitacion.diasDePrueba;
    const cambioElSocio = !edita || socioAEnviar !== (invitacion.socio ?? null);
    setGuardando(true);
    setError(null);
    try {
      const guardada = edita
        ? await adminService.editarInvitacionDemo(invitacion.id, {
            nombre: nombre.trim(),
            topeDeAltas,
            ...(cambioElVencimiento ? { venceEn } : {}),
            ...(cambioElPlazo ? { diasDePrueba } : {}),
            ...(cambioElSocio ? { socio: socioAEnviar } : {}),
          })
        : await adminService.crearInvitacionDemo({
            nombre: nombre.trim(),
            topeDeAltas,
            venceEn,
            diasDePrueba,
            socio: socioAEnviar,
            ...(modoCodigo === 'elegir' ? { codigo: codigo.trim() } : {}),
          });
      onGuardada(guardada);
    } catch (e) {
      setError(mensajeDelServidor(e, 'No se pudo guardar. Prueba de nuevo.'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && !guardando && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{edita ? 'Editar invitación' : 'Nueva invitación'}</DialogTitle>
          <DialogDescription>
            {edita
              ? 'El enlace no cambia: se ajusta el nombre, el tope, el vencimiento, los días de prueba y el socio.'
              : 'Cada invitación es un enlace que puedes repartir a uno o a muchos comercios.'}
          </DialogDescription>
        </DialogHeader>

        {/* Sin la validación del navegador: los avisos son los de `guardar`, en español y con tuteo. */}
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void guardar();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="invitacion-nombre">Nombre</Label>
            <Input
              id="invitacion-nombre"
              value={nombre}
              maxLength={120}
              onChange={(e) => setNombre(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Para reconocerla: a quién o en qué evento la repartes.
            </p>
          </div>

          {!edita && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Código del enlace</legend>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="modo-codigo"
                  checked={modoCodigo === 'generar'}
                  onChange={() => setModoCodigo('generar')}
                />
                Generarlo
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="modo-codigo"
                  checked={modoCodigo === 'elegir'}
                  onChange={() => setModoCodigo('elegir')}
                />
                Elegirlo
              </label>
              {modoCodigo === 'elegir' && (
                <div className="space-y-1.5">
                  <Input
                    aria-label="Código elegido"
                    value={codigo}
                    maxLength={40}
                    onChange={(e) => setCodigo(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Un código fácil de adivinar lo puede usar cualquiera.
                  </p>
                </div>
              )}
            </fieldset>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="invitacion-tope">Tope de altas</Label>
              <Input
                id="invitacion-tope"
                type="number"
                min={1}
                value={tope}
                onChange={(e) => setTope(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Vacío = sin tope.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invitacion-vence">Vence el</Label>
              <Input
                id="invitacion-vence"
                type="date"
                value={vence}
                onChange={(e) => setVence(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Vacío = no vence.</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="invitacion-dias">Días de prueba</Label>
              <Input
                id="invitacion-dias"
                type="number"
                min={MINIMO_DIAS}
                max={MAXIMO_DIAS}
                step={1}
                value={dias}
                onChange={(e) => setDias(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Con 90 el pase dice «3 meses sin costo».
                {edita && ' Sólo vale para las altas que vengan.'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invitacion-socio">Socio</Label>
              <Input
                id="invitacion-socio"
                value={socio}
                maxLength={LARGO_DEL_SOCIO}
                placeholder="Opcional"
                onChange={(e) => setSocio(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Si lo llenas, el pase dice «beneficio exclusivo por tu suscripción a{' '}
                {socio.trim() || '<socio>'}».
              </p>
            </div>
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCerrar} disabled={guardando}>
              Cancelar
            </Button>
            <Button type="submit" disabled={guardando}>
              {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {edita ? 'Guardar cambios' : 'Crear invitación'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
